import { Buffer } from "buffer"
import { setPopLast } from "./array_set"
import { isEmpty } from "./is_empty"
import { serializeError } from "./serialize_error"
import { type AnyObject } from "./types/custom_type.types"

const hasProperty = Object.prototype.hasOwnProperty

const throwsMessage = (error: Error | unknown) =>
  `[Throws: ${error instanceof Error ? error.message : `?`}]`

const safeGetValueFromPropertyOnObject = (object: any, property: string) => {
  if (hasProperty.call(object, property)) {
    try {
      return object[property]
    } catch (error) {
      return throwsMessage(error)
    }
  }

  return object[property]
}

const ensureProperties = (object?: any): any => {
  const seen = new Set()

  const visit = (obj: any): any => {
    if (obj === null || typeof obj !== `object`) {
      if (typeof obj === `bigint`) return Number(obj) || String(obj)

      return obj
    }

    if (seen.has(obj)) return `[Circular]`
    seen.add(obj)

    if (typeof obj.toJSON === `function`) {
      try {
        const result = visit(obj.toJSON())
        seen.delete(obj)
        return result
      } catch (error) {
        return throwsMessage(error)
      }
    }

    if (obj instanceof Error) {
      try {
        const result = visit(serializeError(obj))
        seen.delete(obj)
        return result
      } catch (error) {
        return throwsMessage(error)
      }
    }

    if (Array.isArray(obj)) {
      const result = obj.map(visit)
      seen.delete(obj)
      return result
    }

    const result = Object.keys(obj).reduce((acc, property) => {
      acc[property] = visit(safeGetValueFromPropertyOnObject(obj, property))
      return acc
    }, {} as Record<string, any>)

    seen.delete(obj)
    return result
  }

  return visit(object)
}

const doStringify = (
  data?: any,
  replacer?: (key: any, value: any) => any,
  space?: number | string,
): string =>
  isEmpty(data)
    ? JSON.stringify(data, replacer, space)
    : JSON.stringify(ensureProperties(data), replacer, space)

export const safeJsonStringify = <T = unknown>(
  possiblyObject: T,
  indent = 0,
  replacer?: (key: any, value: any) => any,
): string => {
  if (indent < 0) throw new Error(`indent cannot be negative`)
  if (Buffer.isBuffer(possiblyObject)) return `<<BUFFER>>`

  if (typeof possiblyObject === `string` && /^["{[]/.test(possiblyObject)) {
    try {
      return JSON.parse(possiblyObject)
    } catch {
      //
    }
  }

  try {
    const stringified = doStringify(possiblyObject, replacer, indent)
    return stringified === `""` ? `` : stringified
  } catch {
    return JSON.stringify(String(possiblyObject), replacer, indent)
  }
}

export const safeJsonParse = <R, T = string>(
  string_: T,
  replacer?: (key: any, value: any) => any,
): (R extends AnyObject ? R : T) | undefined => {
  try {
    return JSON.parse(
      typeof string_ === `string` ? string_ : safeJsonStringify(string_),
      replacer,
    )
  } catch {
    return undefined
  }
}
